import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Receipt, MapPin, Phone, Calendar, Printer } from "lucide-react";
import PrintReceipt from "../components/PrintReceipt";

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const STATUS_COLORS = {
  paid: "bg-emerald-100 text-emerald-800 border-emerald-300",
  partial: "bg-amber-100 text-amber-800 border-amber-300",
  unpaid: "bg-rose-100 text-rose-800 border-rose-300",
  cancelled: "bg-slate-200 text-slate-700 border-slate-300",
};

export default function PublicInvoice() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const key = params.get("key");
  const [inv, setInv] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!key) { setErr("Missing access token. Open the link from your WhatsApp message."); return; }
    axios.get(`${API_BASE}/api/public/invoices/${id}?key=${encodeURIComponent(key)}`)
      .then(r => setInv(r.data))
      .catch(e => setErr(e?.response?.data?.detail || "This invoice link is invalid or has expired."));
  }, [id, key]);

  if (err) return <div className="min-h-screen flex items-center justify-center p-6 bg-slate-50"><div className="max-w-md text-center"><Receipt className="w-12 h-12 text-slate-400 mx-auto mb-3" /><div className="font-heading text-xl font-semibold">{err}</div><div className="text-sm text-muted-foreground mt-1">If you think this is a mistake, contact the store.</div></div></div>;
  if (!inv) return <div className="min-h-screen flex items-center justify-center"><div className="text-sm text-muted-foreground">Loading invoice…</div></div>;

  const tax = inv.tax_amount || 0;
  return (
    <div className="min-h-screen bg-slate-50 py-6 sm:py-10 px-4" data-testid="public-invoice-page">
      <div className="max-w-2xl mx-auto mb-3 flex justify-end print:hidden">
        <button onClick={() => window.print()} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-white border border-border text-sm font-semibold hover:bg-slate-50" data-testid="public-print-btn">
          <Printer className="w-4 h-4" />Print receipt
        </button>
      </div>
      <div className="max-w-2xl mx-auto bg-white rounded-2xl shadow-lg overflow-hidden print:hidden">

        {/* Header */}
        <div className="bg-gradient-to-br from-slate-900 to-slate-700 text-white p-6 sm:p-8">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <div className="text-xs uppercase tracking-[0.25em] opacity-70">Invoice</div>
              <div className="font-heading text-2xl sm:text-3xl font-bold mt-1">{inv.invoice_no}</div>
              <div className="text-sm opacity-80 mt-1.5 flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5" />{new Date(inv.created_at).toLocaleString()}</div>
            </div>
            <span className={`text-xs uppercase tracking-widest font-semibold px-2.5 py-1 rounded-full border ${STATUS_COLORS[inv.status] || STATUS_COLORS.unpaid}`}>{inv.status}</span>
          </div>
        </div>

        {/* Branch + Customer */}
        <div className="grid sm:grid-cols-2 gap-4 px-6 sm:px-8 py-5 border-b border-border bg-slate-50">
          {inv.branch && (
            <div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">From</div>
              <div className="font-semibold mt-1">{inv.branch.name}</div>
              {inv.branch.address && <div className="text-xs text-muted-foreground mt-0.5 flex items-start gap-1"><MapPin className="w-3 h-3 mt-0.5 shrink-0" />{inv.branch.address}</div>}
              {inv.branch.phone && <div className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1"><Phone className="w-3 h-3" />{inv.branch.phone}</div>}
              {inv.branch.gst_no && <div className="text-xs text-muted-foreground mt-0.5">GSTIN: {inv.branch.gst_no}</div>}
            </div>
          )}
          {inv.customer && (
            <div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Billed to</div>
              <div className="font-semibold mt-1">{inv.customer.name}</div>
              {inv.customer.mobile_masked && <div className="text-xs text-muted-foreground mt-0.5 font-mono">{inv.customer.mobile_masked}</div>}
            </div>
          )}
        </div>

        {/* Items */}
        <div className="px-6 sm:px-8 py-5">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Items</div>
          <div className="divide-y divide-border" data-testid="invoice-items">
            {inv.items.map((it, i) => (
              <div key={i} className="py-3 flex items-start justify-between gap-3" data-testid={`invoice-item-${i}`}>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm">{it.name}</div>
                  <div className="text-xs text-muted-foreground mt-0.5 font-mono">
                    {it.qty} × ₹{Number(it.unit_price).toFixed(2)}
                    {it.discount_percent > 0 && <span className="text-emerald-700"> · -{it.discount_percent}%</span>}
                    {it.gst_percent > 0 && <span> · GST {it.gst_percent}%</span>}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="font-mono font-semibold">₹{Number(it.final_total).toFixed(2)}</div>
                  {it.tax_amount > 0 && <div className="text-[10px] text-muted-foreground font-mono">incl. ₹{Number(it.tax_amount).toFixed(2)} tax</div>}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Totals */}
        <div className="px-6 sm:px-8 pb-6 space-y-1.5 text-sm">
          <Row label="Subtotal" value={inv.subtotal} />
          {inv.line_discount_amount > 0 && <Row label="Line discount" value={-inv.line_discount_amount} className="text-emerald-700" />}
          {inv.discount_amount > 0 && <Row label="Bill discount" value={-inv.discount_amount} className="text-emerald-700" />}
          {tax > 0 && <Row label="Tax (GST)" value={tax} />}
          <div className="border-t border-border my-2" />
          <Row label="Grand total" value={inv.grand_total} bold className="text-base" />
          <Row label="Paid" value={inv.paid_total} className="text-muted-foreground" />
          {inv.balance > 0 && <Row label="Balance due" value={inv.balance} className="text-rose-700 font-semibold" />}
          {inv.wallet_redeemed > 0 && <Row label="Wallet redeemed" value={-inv.wallet_redeemed} className="text-emerald-700" />}
          {inv.wallet_overpayment > 0 && <Row label="Overpayment → wallet" value={inv.wallet_overpayment} className="text-emerald-700" />}
        </div>

        {/* Payment breakdown */}
        {inv.payments?.length > 0 && (
          <div className="px-6 sm:px-8 pb-6">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1.5">Payments</div>
            <div className="flex flex-wrap gap-2 text-xs">
              {inv.payments.map((p, i) => (
                <div key={i} className="rounded-md border border-border px-2 py-1 bg-slate-50">
                  <span className="capitalize">{p.method}</span> · ₹{Number(p.amount).toFixed(2)}
                  {p.reference && <span className="font-mono text-muted-foreground ml-1">({p.reference})</span>}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="border-t border-border px-6 sm:px-8 py-4 bg-slate-50 text-xs text-muted-foreground flex justify-between items-center flex-wrap gap-2">
          <div>Billed by {inv.billed_by_name || "Staff"}</div>
          <div>Thank you for choosing Furmigo!</div>
        </div>
      </div>
      <PrintReceipt invoice={inv} branch={inv.branch} />
    </div>
  );
}

function Row({ label, value, bold, className = "" }) {
  return (
    <div className={`flex justify-between ${bold ? "font-semibold" : ""} ${className}`}>
      <span>{label}</span>
      <span className="font-mono">{value < 0 ? "-" : ""}₹{Math.abs(value).toFixed(2)}</span>
    </div>
  );
}
